import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Container,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { useSnackbar } from "notistack";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import useStore from "../../store";
import ProfileSection from "../../components/AccountDetails/ProfileSection";
import AddressSection from "../../components/AccountDetails/AddressSection";
import SecuritySection from "../../components/AccountDetails/SecuritySection";
import SubscriptionSection from "../../components/AccountDetails/SubscriptionSection";

const defaultProfile = {
  email: "",
  username: "",
  bio: "",
  first_name: "",
  last_name: "",
  organization_name: "",
  phone_number: ""
};

const defaultAddress = {
  id: 0,
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
  is_primary: "",
};

const defaultSubscription = {
  plan_name: null,
  price: null,
  billing_interval: null,
  expiry_date: null,
  is_active: false,
  features: [],
  pending_cancellation: false
};

export default function AccountDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [profile, setProfile] = useState(defaultProfile);
  const [address, setAddress] = useState(defaultAddress);
  const [updatedProfile, setUpdatedProfile] = useState(defaultProfile);
  const [updatedAddress, setUpdatedAddress] = useState(defaultAddress);
  const [subscription, setSubscription] = useState(defaultSubscription);
  const [inputErrors, setInputErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const activeUser = useStore((state) => state.activeUser);
  const { backgroundColor } = useOutletContext();

  useEffect(() => {
    const fetchData = async () => {
      console.log("activeUser in useEffect:", activeUser);
      if (!activeUser) return;

      try {
        const loadedProfile = await api.getUserProfile(activeUser.username);
        if (loadedProfile.status === 200) {
          const orgName = loadedProfile.data.organization?.name || "";
          const bio = loadedProfile.data?.bio || "";
          const loadedAddress = loadedProfile.data.address || defaultAddress;
          const profileData = {
            ...defaultProfile,
            ...loadedProfile.data,
            bio: bio,
            organization_name: orgName,
            username: activeUser.username,
          };
          setProfile(profileData);
          setUpdatedProfile(profileData);
          setAddress(loadedAddress);
          setUpdatedAddress(loadedAddress);
        }

        // try {
        //   const subscriptionResponse = await api.getUserSubscriptionUsage();
        //   if (subscriptionResponse.hasSubscription) {
        //     setSubscription({
        //       ...defaultSubscription,
        //       plan_name: subscriptionResponse.data.plan_name,
        //       price: subscriptionResponse.data.price,
        //       billing_interval: subscriptionResponse.data.billing_interval,
        //       expiry_date: subscriptionResponse.data.expiry_date,
        //       is_active: subscriptionResponse.data.is_active,
        //       features: subscriptionResponse.data.features,
        //       pending_cancellation: subscriptionResponse.data.pending_cancellation
        //     });
        //   } else {
        //     setSubscription(defaultSubscription);
        //   }
        // } catch (error) {
        //   enqueueSnackbar(<FormattedMessage id="account.subscription_fetch_failed" />, {
        //     variant: "error",
        //     autoHideDuration: 2000,
        //     anchorOrigin: { vertical: "bottom", horizontal: "right" },
        //   });
        // }

        // Fetch login history
        const historyResponse = await api.getActiveSessionData();
        if (historyResponse.status === 200) {
          setLoginHistory(historyResponse.data);
        }
      } catch (error) {
        enqueueSnackbar(<FormattedMessage id="account.profile_fetch_failed" />, {
          variant: "error",
          autoHideDuration: 2000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      }
    };

    fetchData();
  }, [activeUser, enqueueSnackbar]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const updatePaymentStatus = queryParams.get('update_payment_status');
    if (updatePaymentStatus) {
      setTabIndex(1);
    }
  }, [location.search]);

  const handleSave = async () => {
    try {
      const updatedData = {
        ...updatedProfile,
        address_update: updatedAddress,
      };
      const response = await api.updateUserProfile(updatedData);
      if (response.status === 200) {
        setProfile(updatedProfile);
        setAddress(updatedAddress);
        setEditMode(false);
        enqueueSnackbar(<FormattedMessage id="account.profile_saved" />, {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      } else {
        setInputErrors(response.data);
        enqueueSnackbar(<FormattedMessage id="account.profile_save_failed" />, {
          variant: "error",
          autoHideDuration: 2000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      }
    } catch (error) {
      console.error("Failed to save profile:", error);
      enqueueSnackbar(<FormattedMessage id="account.profile_save_failed" />, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
  };

  const handleCancel = () => {
    setUpdatedProfile(profile);
    setUpdatedAddress(address);
    setEditMode(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (newValue) => {
    setUpdatedProfile((prev) => ({
      ...prev,
      phone_number: newValue,
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setUpdatedAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeTab = (event, newIndex) => setTabIndex(newIndex);

  const updateLoginHistory = (updatedHistory) => {
    console.log("Updating loginHistory with:", updatedHistory);
    setLoginHistory(updatedHistory);
  };

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: "#f5f5f5", px: 3 }}>
      <Container
        maxWidth="lg"
        sx={{
          mt: 4,
          px: 2,
          py: 4,
          maxWidth: "1220px",
          margin: "auto",
          backgroundColor: backgroundColor,
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={handleChangeTab}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab label={<FormattedMessage id="account.account_details" defaultMessage="Account Details" />} />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {tabIndex === 0 && (
            <Card elevation={3} sx={{ borderRadius: 2, p: 3 }}>
              <CardContent>
                <ProfileSection
                  profile={profile}
                  updatedProfile={updatedProfile}
                  inputErrors={inputErrors}
                  editMode={editMode}
                  onEditClick={() => setEditMode(true)}
                  handleInputChange={handleInputChange}
                  handlePhoneChange={handlePhoneChange}
                />
                <AddressSection
                  address={address}
                  updatedAddress={updatedAddress}
                  inputErrors={inputErrors}
                  editMode={editMode}
                  handleAddressChange={handleAddressChange}
                />
                {editMode && (
                  <Box display="flex" gap={2} sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Save />}
                      onClick={handleSave}
                    >
                      <FormattedMessage id="account.save_changes" defaultMessage="Save Changes" />
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                    >
                      <FormattedMessage id="account.cancel" defaultMessage="Cancel" />
                    </Button>
                  </Box>
                )}
                <SecuritySection
                  loginHistory={loginHistory}
                  updateLoginHistory={updateLoginHistory}
                />
              </CardContent>
            </Card>
          )}

        </Box>
      </Container>
    </Box>
  );
}
